/**
 * App version + release history.
 *
 * `APP_VERSION` is rendered as a small chip in the UI (see
 * `VersionBadge`) so it's always possible to tell which build is live
 * without opening devtools — handy when verifying that a deploy
 * actually reached the browser past the service-worker cache.
 *
 * Bump `APP_VERSION` and prepend a `CHANGELOG` entry with every
 * deploy. Newest entry first; the badge reads `CHANGELOG[0]` for the
 * "what's new" summary.
 */
export const APP_VERSION = "0.3.6";

export interface ReleaseNote {
  version: string;
  /** ISO yyyy-mm-dd. */
  date: string;
  changes: string[];
}

export const CHANGELOG: ReleaseNote[] = [
  {
    version: "0.3.6",
    date: "2026-08-03",
    changes: [
      "Weighted APY (range) now matches Range return exactly on a 1Y selection — picking \"1Y\" slices a 364-day window, and annualising it to 365 nudged the APY up a tenth of a point. Shorter ranges still annualise as before.",
      "MRec added to the main metrics cards (lifetime), not just the range block — show/hide it from the Manage metrics dialog."
    ]
  },
  {
    version: "0.3.5",
    date: "2026-08-03",
    changes: [
      "New MRec (max recovery time) metric card — the longest stretch spent below a prior peak, with SPY comparison and an ongoing-drawdown indicator."
    ]
  },
  {
    version: "0.3.4",
    date: "2026-08-03",
    changes: [
      "The Range return card's info tooltip said \"Weekly TWR\" — it was wired to the wrong term. It now explains the range return and its relationship to Weighted APY (range).",
      "Range Max Drawdown and Sharpe now rebase geometrically too; all rebasing routes through one shared helper so chips, lines and cards can't drift apart."
    ]
  },
  {
    version: "0.3.3",
    date: "2026-08-03",
    changes: [
      "Chart chips now agree with the metrics cards: rebasing a range compounds the return instead of subtracting cumulative percentages (SPY read +24.66% on the chip vs +20.04% on the card).",
      "Applies to the Portfolio, SPY and SPY+D lines — the plotted lines were overstated on any range that didn't start at inception."
    ]
  },
  {
    version: "0.3.2",
    date: "2026-08-03",
    changes: [
      "Target line now pro-rates to the account's actual history when it's shorter than the selected range — a 1Y view of a 4-month-old account showed the full +20% target instead of ~+6%.",
      "Full-length windows keep the clean broker-style numbers (3M at 20%/yr still reads +5%)."
    ]
  },
  {
    version: "0.3.1",
    date: "2026-08-03",
    changes: [
      "Equity curve now uses Alpaca's own daily snapshots wherever they exist, instead of rebuilding history from WHB's trade feed — which couldn't see pre-WHB activity and left the curve ~$77k low (1Y started at ~$2.6k where Alpaca shows ~$80k).",
      "Reconstruction is now used only outside Alpaca's window: deep history before the first snapshot, and today's tail (joined to Alpaca's last value so the splice adds no step).",
      "Console now reports Alpaca-vs-reconstruction divergence per account."
    ]
  },
  {
    version: "0.3.0",
    date: "2026-08-03",
    changes: [
      "Fixed the final-day equity cliff: a systematic reconstruction bias was being anchored to the last day only, dropping the curve ~$24.6k on the final point. Large corrections now shift the whole curve.",
      "Fixed phantom cash from orphan sells — sells whose matching buy is missing from the orders feed credited full proceeds, inflating equity by ~$25k.",
      "Fixed window return % on live accounts (1Y showed +4006% instead of ~+35%).",
      "First and last chart points are now selectable at every range, with their markers fully visible.",
      "Added this version badge + release history."
    ]
  },
  {
    version: "0.2.0",
    date: "2026-05-21",
    changes: [
      "Algo Builder: import a symphony JSON and browse it as a paginated tree; export/copy JSON.",
      "Marketplace: multi-step Buy flow (amount → wizard settings → broker → run), watch list, per-broker connect flows.",
      "Bot actions wired up: Buy/Sell with order timing, Go to cash, Skip trading, Watch, Share.",
      "Renamed Structure to Algo; removed per-bot pricing."
    ]
  }
];
